/**
 * La consola en vivo.  (US1, FR-001..FR-008, FR-055)  🎯 MVP
 *
 * Tabla densa, ordenada por gravedad (los problemas arriba, SC-002). No sondea:
 * la carga inicial es una consulta; a partir de ahí, el `ClienteVivo` del Layout
 * empuja las filas a esta misma caché por SSE (lib/live.ts).
 *
 * El tráfico del laboratorio de personas se OCULTA por defecto y se muestra con
 * el interruptor, siempre marcado (FR-055). El nº de conversación enlaza a
 * Chatwoot (FR-019); el nombre del contacto NO está aquí (SC-011) — se pintará
 * en vivo en el detalle (US2).
 */
import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { Link, useParams } from 'react-router'

import { useSesion } from '../auth'
import { useEsAdminUi } from '../ver-como'
import { useFichaNegocio } from '../lib/ficha-negocio'
import { useIncluirTestsVista } from '../lib/use-incluir-tests'
import { CountersCell } from '../components/CountersCell'
import { OutcomeBadge } from '../components/OutcomeBadge'
import { fraseDeFase } from '../lib/fase'
import { Badge } from '../components/ui/badge'
import { Button } from '../components/ui/button'
import { api } from '../lib/api'
import { claveConversaciones } from '../lib/live'
import { haceCuanto } from '../lib/tiempo'
import type { FilaConversacion, RespuestaLista } from '../lib/tipos'

const COLUMNAS = ['Conversación', 'Negocio', 'Flujo · estado', 'Atascos', 'Desenlace', 'Última obs.']

export function Conversations() {
    const { accountId } = useParams()
    const cuenta = Number(accountId)
    const { data: sesion } = useSesion()
    const esAdmin = useEsAdminUi()
    const { esDemo } = useFichaNegocio(accountId)
    const { incluirTests: mostrarTests, cambiar, mostrarInterruptor, listo } = useIncluirTestsVista(accountId, esDemo, esAdmin === true)

    // La clave de caché es la DEL NEGOCIO, y el flujo SSE escribe en esa misma
    // (live.ts), así que el vivo sigue casando. El interruptor cambia lo que PIDE
    // el servidor (solo reales, o todo) y fuerza una recarga — así las reales no
    // quedan fuera del límite por culpa del tráfico de test más reciente.
    const { data, isPending, isError, refetch } = useQuery({
        queryKey: claveConversaciones(cuenta),
        queryFn: () =>
            api.get<RespuestaLista>(`/conversations?accountId=${cuenta}&includeTest=${mostrarTests}&limit=200`),
        enabled: esAdmin === true && listo,
    })
    useEffect(() => {
        void refetch()
    }, [mostrarTests, refetch])

    if (sesion && !esAdmin) {
        return <p className="text-sm text-neutral-500">No encontrado</p>
    }
    if (isPending || !data) return <p className="text-sm text-neutral-500">Cargando conversaciones…</p>
    if (isError) return <p className="text-sm text-red-400">No se pudieron cargar las conversaciones.</p>

    // El servidor ya filtra; este filtro es red de seguridad (el SSE puede colar
    // una fila de test en la caché mientras estamos en modo "solo reales").
    const visibles = mostrarTests ? data.items : data.items.filter((c) => !c.synthetic)

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <div className="flex items-baseline gap-2 text-xs text-neutral-500">
                    <span className="text-neutral-200">{data.realCount}</span> reales
                    <span className="text-neutral-700">·</span>
                    <span className="text-neutral-400">{data.testCount}</span> de test
                    {!mostrarTests && data.testCount > 0 && <span className="text-neutral-700">(ocultas)</span>}
                </div>
                {mostrarInterruptor && (
                    <Button variant="outline" size="sm" onClick={() => cambiar(!mostrarTests)}>
                        {mostrarTests ? 'Ocultar tests' : 'Mostrar tests'}
                    </Button>
                )}
            </div>

            <div className="overflow-x-auto rounded-lg border border-neutral-800">
                <table className="tabla-carta w-full border-collapse text-sm">
                    <thead>
                        <tr className="border-b border-neutral-800 text-left text-xs text-neutral-500">
                            {COLUMNAS.map((c) => (
                                <th key={c} className="px-3 py-2 font-medium">
                                    {c}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {visibles.length === 0 ? (
                            <tr>
                                <td colSpan={COLUMNAS.length} className="px-3 py-6 text-center text-neutral-600">
                                    {data.realCount === 0 && data.testCount > 0
                                        ? 'Solo hay tráfico de test. Pulsa "Mostrar tests" para verlo.'
                                        : 'Ninguna conversación viva ahora mismo.'}
                                </td>
                            </tr>
                        ) : (
                            visibles.map((c) => <Fila key={c.conversationId} c={c} cuenta={cuenta} />)
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

function Negocio({ c }: { c: FilaConversacion }) {
    if (c.businessName) return <span className="text-neutral-300">{c.businessName}</span>
    if (c.synthetic) return <span className="text-neutral-700">—</span>
    return <span className="text-neutral-600 italic">resolviendo…</span>
}

function Fila({ c, cuenta }: { c: FilaConversacion; cuenta: number }) {
    return (
        <tr data-testid="fila-conversacion" data-outcome={c.outcome} data-synthetic={c.synthetic} className="border-b border-neutral-900 hover:bg-neutral-900/50">
            <td className="px-3 py-2 font-medium text-neutral-200" data-label="Conversación">
                <span className="flex items-center gap-2">
                    {/* El número abre la FICHA del panel (más rica). El ↗ salta directo a Chatwoot. */}
                    <Link to={`/business/${cuenta}/console/${c.conversationId}`} className="text-neutral-200 hover:text-neutral-50 hover:underline">
                        #{c.conversationId}
                    </Link>
                    {c.chatwootUrl && (
                        <a
                            href={c.chatwootUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-sky-500 hover:text-sky-400"
                            title="Abrir en Chatwoot"
                        >
                            ↗
                        </a>
                    )}
                    {c.synthetic && <Badge tone="info">test</Badge>}
                </span>
            </td>
            <td className="px-3 py-2" data-label="Negocio">
                <Negocio c={c} />
            </td>
            <td className="px-3 py-2 text-neutral-300" data-label="Flujo · estado">
                <span className="text-neutral-200">{fraseDeFase(c.currentState ?? c.activeFlow)}</span>
            </td>
            <td className="px-3 py-2" data-label="Atascos">
                <CountersCell counters={c.counters} />
            </td>
            <td className="px-3 py-2" data-label="Desenlace">
                <span className="flex flex-col gap-0.5">
                    <OutcomeBadge outcome={c.outcome} />
                    {c.outcomeReason && <span className="text-[10px] leading-tight text-neutral-600">{c.outcomeReason}</span>}
                </span>
            </td>
            <td className="px-3 py-2 whitespace-nowrap text-xs text-neutral-500" data-label="Última obs.">{haceCuanto(c.lastObservedAt)}</td>
        </tr>
    )
}
